import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapPin, X } from 'lucide-react';
import { Field, useDebounced } from '@/components/live';
import { geographyById, geographyParents, geographyPath, geographySearch, type GeographyHit } from '@/api/lookups';
import { isSupabaseConfigured } from '@/lib/supabase';

/**
 * One box to find a place (owner, 2026-09-25: "Can we have combined display as a single dropdown.
 * The user can search by typing any text? Which filters the value, but in back end it splits").
 *
 * It replaced State -> District -> Block -> Village: four decisions to record one fact, when the
 * officer almost always knows the village name and nothing else, and the first three were only
 * ever a way to narrow the fourth. Type any part of any level - the village, its block, its
 * district, its state - and each answer shows the whole path, so two villages of the same name
 * are told apart by what is beside them. What is saved is still a single village id.
 *
 * ONLY WHAT THE ORGANISATION HAS ACTIVATED appears, and only within the caller's picker scope:
 * `api.fn_geography_search` applies exactly the rules the cascading lists applied. That rule is
 * why the cascade existed - it is what stops a farmer being enrolled into a district the
 * organisation never took on - and a search that ignored it would be a way around it.
 */
export function GeographyPicker({
  value,
  onChange,
  error,
  label = 'Village',
  required = true,
  geographyType = 'village',
  hint,
}: {
  value: string;
  onChange: (geographyId: string) => void;
  error?: string;
  /**
   * WHICH LEVEL is being chosen. A farmer lives in a village, but an office SERVES an area, and
   * the office nearest a reader is matched on state - so asking an admin to name a village for a
   * head office would be a question whose answer is then thrown away. `fn_geography_search`
   * already takes the level; this passes it through.
   */
  geographyType?: 'state' | 'district' | 'block' | 'village';
  /** Overrides the default explanation, which talks about villages. */
  hint?: string;
  /**
   * Whether the asterisk is shown. It was hardcoded on, which was true of every caller until the
   * organisation profile, where "Based in" is optional - and an asterisk on an optional field is a
   * small lie that somebody acts on.
   */
  required?: boolean;
  label?: string;
}) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const term = useDebounced(query).trim();

  // The place already on the record, so an existing farmer opens showing where they live rather
  // than an empty box.
  const chosen = useQuery({
    queryKey: ['geo', 'by-id', value],
    queryFn: () => geographyById(value),
    enabled: isSupabaseConfigured && Boolean(value),
  });

  const hits = useQuery({
    // The level is part of the key: without it a state search would be served the villages
    // cached under the same term.
    queryKey: ['geo', 'search', geographyType, term],
    queryFn: () => geographySearch(term, geographyType),
    enabled: isSupabaseConfigured && open && term.length >= 2,
  });

  // What the reader is being asked for, in their own words rather than "geography".
  const noun = geographyType === 'village' ? 'village' : geographyType;
  const searchable = geographyType === 'village'
    ? 'village, block, district or state'
    : `${noun} - any part of the name`;

  const options = useMemo(() => hits.data ?? [], [hits.data]);
  useEffect(() => { setActive(0); }, [term]);

  // Close on an outside click, as the header's pickers do.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  function choose(hit: GeographyHit) {
    onChange(hit.id);
    setQuery('');
    setOpen(false);
    input.current?.blur();
  }

  function clear() {
    onChange('');
    setQuery('');
    setOpen(true);
    input.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') { setOpen(false); return; }
    if (!options.length) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((at) => (at + 1) % options.length); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((at) => (at - 1 + options.length) % options.length); }
    else if (event.key === 'Enter') { event.preventDefault(); const hit = options[active]; if (hit) choose(hit); }
  }

  const settled = value && chosen.data ? geographyPath(chosen.data) : '';

  return (
    <Field
      error={error}
      hint={hint ?? (geographyType === 'village'
        ? 'Type a village, block, district or state - any part of the name.'
        : `Type a ${noun} name.`)}
      label={label}
      required={required}
    >
      <div className="geo-pick" ref={box}>
        {value && !open ? (
          // Settled: the whole path, and one button to change it. Nothing to re-choose by accident.
          <div className="geo-pick-chosen">
            <MapPin size={15} />
            <span>{chosen.isLoading ? 'Loading...' : settled || 'A place is set'}</span>
            <button aria-label={`Change ${label.toLowerCase()}`} onClick={clear} type="button"><X size={15} /></button>
          </div>
        ) : (
          <>
            <input
              aria-activedescendant={open && options[active] ? `${listId}-${active}` : undefined}
              aria-autocomplete="list"
              aria-controls={listId}
              aria-expanded={open}
              autoComplete="off"
              onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              placeholder={`Search a ${searchable}`}
              ref={input}
              role="combobox"
              type="text"
              value={query}
            />
            {open && (
              <ul className="geo-pick-list" id={listId} role="listbox">
                {term.length < 2 && <li className="geo-pick-note">Type at least two letters.</li>}
                {term.length >= 2 && hits.isLoading && <li className="geo-pick-note">Searching...</li>}
                {term.length >= 2 && !hits.isLoading && options.length === 0 && (
                  <li className="geo-pick-note">
                    Nothing matches "{term}". Only places this organisation has activated are listed.
                  </li>
                )}
                {options.map((hit, index) => (
                  <li
                    aria-selected={index === active}
                    className={index === active ? 'is-active' : undefined}
                    id={`${listId}-${index}`}
                    key={hit.id}
                    onMouseDown={(event) => { event.preventDefault(); choose(hit); }}
                    onMouseEnter={() => setActive(index)}
                    role="option"
                  >
                    <strong>{hit.name}</strong>
                    <small>{geographyParents(hit)}</small>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Field>
  );
}
